import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { apptApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Stat, StatusBadge } from "../../Components/UI/ui";
import { BOARD_COLUMNS, GATE, STATUS_LABEL, todayStr, addDays, fmtTime, cap } from "../../lib/lab";
import AppointmentForm from "../Appointments/AppointmentForm";
import AppointmentDetail from "../Appointments/AppointmentDetail";

// Today board: every booking scheduled on the chosen day, grouped by stage.
function Home() {
  const { can } = useAuth();
  const { toast } = useApp();
  const [date, setDate] = useState(todayStr());
  const [serviceType, setServiceType] = useState("");
  const [search, setSearch] = useState("");
  const [appts, setAppts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { startDate: date, endDate: date, limit: 200 };
      if (serviceType) params.serviceType = serviceType;
      if (search.trim()) params.search = search.trim();
      const [list, sum] = await Promise.all([apptApi.list(params), apptApi.summary({ startDate: date, endDate: date })]);
      setAppts(list.appointments || []);
      setSummary(sum);
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  }, [date, serviceType, search, toast]);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);

  const columns = useMemo(
    () => BOARD_COLUMNS.map((c) => ({ ...c, items: appts.filter((a) => c.statuses.includes(a.status)) })),
    [appts]
  );
  const dropped = appts.filter((a) => ["CANCELLED", "NO_SHOW"].includes(a.status));
  const isToday = date === todayStr();
  const by = summary?.byStatus || {};

  return (
    <Page
      title={isToday ? "Today" : new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
      subtitle={summary ? `${summary.total} booking${summary.total === 1 ? "" : "s"}` : ""}
      actions={
        <>
          <input className="lab-input" style={{ width: 280 }} placeholder="Search patient name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, -1))}>‹</Button>
          <input type="date" className="lab-input" value={date} onChange={(e) => setDate(e.target.value)} />
          <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, 1))}>›</Button>
          {!isToday && <Button size="sm" onClick={() => setDate(todayStr())}>Today</Button>}
          <select className="lab-select" value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
            <option value="">All services</option>
            <option value="pathology">Pathology</option>
            <option value="radiology">Radiology</option>
          </select>
          {can(...GATE.book) && (
            <Button variant="primary" onClick={() => setBookOpen(true)}><IoMdAdd /> New booking</Button>
          )}
        </>
      }
    >
      <div className="lab-stats">
        <Stat accent label="Total" value={summary?.total ?? "—"} sub={loading ? "Refreshing…" : ""} />
        <Stat label="Awaiting confirmation" value={by.REQUESTED ?? 0} sub="doctor referrals" />
        <Stat label="Upcoming" value={by.CONFIRMED ?? 0} />
        <Stat label="Home collections" value={summary?.pendingHomeCollections ?? 0} sub="pending pickup" />
        <Stat label="In process" value={(by.CHECKED_IN ?? 0) + (by.SAMPLE_COLLECTED ?? 0) + (by.IN_PROCESS ?? 0)} />
        <Stat label="Report ready" value={by.REPORT_READY ?? 0} sub="to hand over" />
        <Stat label="Completed" value={by.COMPLETED ?? 0} />
      </div>

      <div className="lab-page__body">
        <div className="lab-board">
          {columns.map((col) => (
            <div className="lab-col" key={col.id}>
              <div className="lab-col__head">
                <span>{col.title}</span>
                <span className="n">{col.items.length}</span>
              </div>
              <div className="lab-col__body">
                {col.items.map((a) => (
                  <ApptCard key={a._id} a={a} onClick={() => setSelected(a._id)} />
                ))}
                {col.items.length === 0 && <div className="lab-muted" style={{ padding: "12px 4px" }}>—</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {dropped.length > 0 && (
        <div className="lab-row">
          <span className="lab-muted">Cancelled / no-show:</span>
          {dropped.map((a) => (
            <button key={a._id} className="lab-link" onClick={() => setSelected(a._id)}>
              {a.patientName} <span className="lab-muted">({STATUS_LABEL[a.status]})</span>
            </button>
          ))}
        </div>
      )}

      <AppointmentForm open={bookOpen} onClose={() => setBookOpen(false)} onSaved={load} initialDate={date} />
      <AppointmentDetail id={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </Page>
  );
}

const ApptCard = ({ a, onClick }) => (
  <div className={`lab-appt ${a.serviceType}`} onClick={onClick}>
    <div className="lab-row between">
      <span className="name">{a.patientName}</span>
      <span className={`lab-dot ${a.paymentStatus || "unpaid"}`} title={`Payment: ${a.paymentStatus || "unpaid"}`} />
    </div>
    <div className="meta">
      <span>{fmtTime(a.scheduledAt)}</span>
      <span>·</span>
      <span>{cap(a.serviceType)}</span>
      {a.collectionType === "home" && <Badge kind="home">Home</Badge>}
      {a.source === "doctor_referral" && <Badge kind="REQUESTED">Referral</Badge>}
    </div>
    <div className="items">{(a.items || []).map((i) => i.name).join(", ")}</div>
    {a.sampleId && <div className="meta" style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{a.sampleId}</div>}
    <div style={{ marginTop: 6 }}><StatusBadge status={a.status} /></div>
  </div>
);

export default Home;
